import { FaYoutube, FaInstagram } from "react-icons/fa";
import { useTranslation } from "react-i18next";

export function LandingPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-pink-900/20"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Hero Section - Centered */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-20">
          
          {/* Title */}
          <h1 className="text-7xl md:text-8xl font-extralight mb-8 text-center tracking-tight">
            <span className="bg-gradient-to-r from-white via-purple-200 to-pink-200 text-transparent bg-clip-text">
              {t('landingPage.title')}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl font-light mb-16 text-center text-gray-300 max-w-2xl tracking-wide">
            {t('landingPage.description')}
          </p>

          {/* Centered Samantha Image */}
          <div className="mb-16">
            <div className="relative group cursor-pointer">
              <div className="absolute -inset-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur-xl opacity-40 group-hover:opacity-80 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
              <div className="absolute -inset-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-lg opacity-30 group-hover:opacity-70 transition duration-1000 group-hover:duration-200 animate-pulse" style={{animationDelay: '0.8s'}}></div>
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-300 to-pink-300 rounded-full blur-md opacity-20 group-hover:opacity-50 transition duration-1000 group-hover:duration-200 animate-pulse" style={{animationDelay: '1.6s'}}></div>
              <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border border-white/10 bg-gradient-to-br from-gray-900 to-black">
                <img 
                  src="https://publicsamassets.s3.amazonaws.com/img/sam_ai_sapiens.jpg"
                  alt="Samantha"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mb-20">
            <a
              href="/product/2"
              className="inline-flex items-center px-12 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-lg font-medium rounded-full hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-purple-500/25"
            >
              Start Chatting
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>

        {/* Testimonials - Minimal */}
        <div className="px-4 py-16 border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-light text-center mb-16 text-gray-300">
              {t('landingPage.testimonials.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Testimonial 1 */}
              <div className="text-center group">
                <p className="text-gray-400 mb-6 text-lg leading-relaxed font-light">
                  "{t('landingPage.testimonials.testimonial1.text')}"
                </p>
              </div>

              {/* Testimonial 2 */}
              <div className="text-center group">
                <p className="text-gray-400 mb-6 text-lg leading-relaxed font-light">
                  "{t('landingPage.testimonials.testimonial2.text')}"
                </p>
              </div>

              {/* Testimonial 3 */}
              <div className="text-center group">
                <p className="text-gray-400 mb-6 text-lg leading-relaxed font-light">
                  "{t('landingPage.testimonials.testimonial3.text')}"
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Social Links - Minimal */}
        <div className="px-4 py-12 border-t border-white/5">
          <div className="text-center">
            <p className="text-gray-400 mb-6 font-light">{t('landingPage.followText')}</p>
            <div className="flex justify-center space-x-8">
              <a
                href="https://www.youtube.com/@samanthahilbert/videos"
                target="_blank"
                rel="noopener noreferrer"
                className="group p-3 rounded-full border border-white/10 hover:border-red-500/50 transition-all duration-300"
              >
                <FaYoutube
                  size={24}
                  className="text-gray-400 group-hover:text-red-400 transition-colors duration-300"
                />
              </a>
              <a
                href="https://www.instagram.com/sam.ai.ht/"
                target="_blank"
                rel="noopener noreferrer"
                className="group p-3 rounded-full border border-white/10 hover:border-pink-500/50 transition-all duration-300"
              >
                <FaInstagram
                  size={24}
                  className="text-gray-400 group-hover:text-pink-400 transition-colors duration-300"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
